import {
  useContext,
  useState,
  Fragment,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { Dialog, Transition } from "@headlessui/react";
import { MobXProviderContext } from "mobx-react";
import classNames from "classnames";
import { toJS } from "mobx";
import { observer } from "mobx-react";
import { InfoButtonGray } from "assets/img";
import {
  getValidMealListForSubsidy,
  useMealNextAvailableDateForSubsidy,
  useMealNextAvailableDate,
  getValidMealList,
} from "utils/hooks/useMealNextAvailableDate";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { Calendar } from "react-multi-date-picker";
import dayjs from "dayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import object from "utils/helpers/object";
import { priceFormatter } from "utils/helpers/validation";
import { useSubsidyControl } from "utils/hooks/useSubsidyControl";
import { preventMinus, preventPasteNegative } from "utils/helpers/validation";
import { isPresent } from "utils/helpers/array";

function ChangeSubsidyMealPlan({
  visible,
  setModal,
  bulkUserUpdate = false,
  bulkUsers,
  updatingMeal = false,
  meal,
  meals,
}) {
  const [subsidyType, setSubsidyType] = useState("daily");

  const [weeklySubsidy, setWeeklySubsidy] = useState(0);

  const [standardSubsidy, setStandardSubsidy] = useState(0);

  const [enablePerMealWeeklySubsidy, setEnabledPerMealWeekly] = useState(false);

  const [overPayment, setOverPayment] = useState(true);

  const { mealManagementStore } = useContext(MobXProviderContext);

  const mealPlan = mealManagementStore.mealPlan;

  const { homeStore } = useContext(MobXProviderContext);

  const { userStore } = useContext(MobXProviderContext);

  const { locationID, defaultLocation } = userStore;

  const calendarRef = useRef(null);
  const mealDateUser = useMealNextAvailableDate(meals);
  const mealDateSubsidy = useMealNextAvailableDateForSubsidy(meals);
  const validMealsUser = getValidMealList(meals);
  const validMealsSubsidy = getValidMealListForSubsidy(meals);
  const nextAvailableMeal = bulkUserUpdate ? mealDateUser : mealDateSubsidy;
  const validMeals = bulkUserUpdate ? validMealsUser : validMealsSubsidy;
  const [changeEffect, setChangeEffect] = useState("next");
  const [selectedCustomDate, setSelectedCustomDate] = useState(null);
  const [warning, setWarning] = useState(false);
  const [weeklyError, setWeeklyError] = useState(false);

  const [standardSubsidyError, setStandardSubsidyError] = useState(false);

  const enabledDays = useMemo(() => {
    return mealPlan?.attributes?.days || [];
  }, [mealPlan]);

  const {
    dailySubsidy,
    onChangeDaily,
    isDayEnabled,
    setSubsidyManually,
    validate,
    errors,
  } = useSubsidyControl(enabledDays);

  useEffect(() => {
    if (!bulkUserUpdate) {
      if (updatingMeal) {
        const rawSubsidy = toJS(meal?.attributes?.daily_subsidy) || {};
        const formattedSubsidy = Object.fromEntries(
          Object.entries(rawSubsidy)?.map(([day, value]) => [
            day,
            parseFloat(value).toFixed(2),
          ])
        );

        setSubsidyManually(formattedSubsidy);
        setSubsidyType(meal?.attributes?.subsidy_type ?? "daily");
        setWeeklySubsidy(meal?.attributes?.subsidy ?? 0);
        setStandardSubsidy(meal?.attributes?.standard_subsidy ?? 0);
        setOverPayment(meal?.attributes?.employee_pays_by_credit_card);
      } else {
        const rawSubsidy = toJS(mealPlan?.attributes?.daily_subsidy) || {};
        const formattedSubsidy = Object.fromEntries(
          Object.entries(rawSubsidy)?.map(([day, value]) => [
            day,
            parseFloat(value).toFixed(2),
          ])
        );

        setSubsidyManually(formattedSubsidy);
        setSubsidyType(mealPlan?.attributes?.subsidy_type ?? "daily");
        setWeeklySubsidy(mealPlan?.attributes?.subsidy ?? 0);
        setStandardSubsidy(mealPlan?.attributes?.standard_subsidy ?? 0);
        setOverPayment(mealPlan?.attributes?.employee_pays_by_credit_card);
      }
    } else {
      setSubsidyManually({
        monday: "0.00",
        tuesday: "0.00",
        wednesday: "0.00",
        thursday: "0.00",
        friday: "0.00",
        saturday: "0.00",
        sunday: "0.00",
      });
    }
  }, [mealPlan]);

  const onSavePressStandardSubsidy = () => {
    if (
      standardSubsidy == "" ||
      standardSubsidy === null ||
      standardSubsidy === undefined
    ) {
      setStandardSubsidyError(true);
      return;
    } else {
      setStandardSubsidyError(false);
    }

    if (!warning && object.isPresent(nextAvailableMeal)) {
      setWarning(true);
    } else {
      let payload = {
        meal_series: {
          standard_subsidy: standardSubsidy,
          employee_pays_by_credit_card: overPayment,
        },
      };

      if (object.isPresent(nextAvailableMeal)) {
        payload.meal_series.effective_date =
          changeEffect == "custom"
            ? selectedCustomDate ?? nextAvailableMeal?.mealDate
            : nextAvailableMeal?.mealDate;
      }

      mealManagementStore
        .updateMealPlan(locationID, mealPlan?.id, payload)
        .then(() => setModal(false));
    }
  };

  const onSavePressBothSubsidies = () => {
    if (subsidyType === "daily" && !validate()) {
      return;
    }

    if (
      standardSubsidy == "" ||
      standardSubsidy === null ||
      standardSubsidy === undefined
    ) {
      setStandardSubsidyError(true);
      return;
    } else {
      setStandardSubsidyError(false);
    }

    if (subsidyType == "weekly") {
      if (
        weeklySubsidy == "" ||
        weeklySubsidy === null ||
        weeklySubsidy === undefined
      ) {
        setWeeklyError(true);
        return;
      } else {
        setWeeklyError(false);
      }
    }

    if (!warning && object.isPresent(nextAvailableMeal)) {
      setWarning(true);
    } else {
      let payload = {
        meal_series: {
          subsidy_type: subsidyType,
          employee_pays_by_credit_card: overPayment,
          standard_subsidy: standardSubsidy,
        },
      };

      if (subsidyType == "daily") {
        payload.meal_series.daily_subsidy = dailySubsidy;
        payload.meal_series.subsidy = 0;
      } else if (subsidyType == "weekly") {
        payload.meal_series.daily_subsidy = {};
        payload.meal_series.subsidy = weeklySubsidy;
      }

      if (object.isPresent(nextAvailableMeal)) {
        payload.meal_series.effective_date =
          changeEffect == "custom"
            ? selectedCustomDate ?? nextAvailableMeal?.mealDate
            : nextAvailableMeal?.mealDate;
      }

      mealManagementStore
        .updateMealPlan(locationID, mealPlan?.id, payload)
        .then(() => setModal(false));
    }
  };

  const onSavePress = () => {
    if (
      (!isPresent(mealPlan?.attributes?.days) &&
        isPresent(mealPlan?.attributes?.dates_to_add) &&
        !bulkUserUpdate) ||
      isPresent(mealPlan?.attributes?.dates_to_add)
    ) {
      onSavePressStandardSubsidy();
      return;
    }

    if (
      isPresent(mealPlan?.attributes?.days) &&
      isPresent(mealPlan?.attributes?.dates_to_add) &&
      enablePerMealWeeklySubsidy &&
      !bulkUserUpdate
    ) {
      onSavePressBothSubsidies();
      return;
    }

    if (subsidyType === "daily" && !validate()) {
      return;
    }

    if (subsidyType == "weekly") {
      if (
        weeklySubsidy == "" ||
        weeklySubsidy === null ||
        weeklySubsidy === undefined
      ) {
        setWeeklyError(true);
        return;
      } else {
        setWeeklyError(false);
      }
    }

    if (!warning && object.isPresent(nextAvailableMeal)) {
      setWarning(true);
    } else if (bulkUserUpdate) {
      let payload = {
        ids: bulkUsers?.map((user) => user?.id),
        employee_meal_plan: {
          subsidy_type: subsidyType,
          daily_subsidy: dailySubsidy,
          subsidy: weeklySubsidy,
        },
        meal_series_id: mealPlan?.id,
      };

      if (object.isPresent(nextAvailableMeal)) {
        payload.effective_date =
          changeEffect == "custom"
            ? selectedCustomDate ?? nextAvailableMeal?.mealDate
            : nextAvailableMeal?.mealDate;
      }

      mealManagementStore
        .bulkSubsidyUpdate(locationID, mealPlan?.id, payload)
        .then(() => setModal(false));
    } else {
      let payload = {
        meal_series: {
          subsidy_type: subsidyType,
          employee_pays_by_credit_card: overPayment,
        },
      };

      if (subsidyType == "daily") {
        payload.meal_series.daily_subsidy = dailySubsidy;
        payload.meal_series.subsidy = 0;
      } else if (subsidyType == "weekly") {
        payload.meal_series.daily_subsidy = {};
        payload.meal_series.subsidy = weeklySubsidy;
      }

      if (object.isPresent(nextAvailableMeal)) {
        payload.meal_series.effective_date =
          changeEffect == "custom"
            ? selectedCustomDate ?? nextAvailableMeal?.mealDate
            : nextAvailableMeal?.mealDate;
      }

      mealManagementStore
        .updateMealPlan(locationID, mealPlan?.id, payload)
        .then(() => setModal(false));
    }
  };

  const setEnableOverpayment = (value) => {
    setOverPayment(value);
  };

  const handleDateChange = (value) => {
    const selectedDate = dayjs(value.toDate()).format("YYYY-MM-DD");

    setSelectedCustomDate(selectedDate);
  };

  return (
    <Transition appear show={visible} as={Fragment} avoidClose={true}>
      <Dialog
        as="div"
        className="relative z-[90]"
        onClose={() => {
          // setModal(false)
        }}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center sm:p-4 md:p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              {warning ? (
                <Dialog.Panel className="w-[696px] min-h-[276px] d-col justify-between rounded-xl md:w-6/12 transform overflow-hidden sm:rounded-2xl md:rounded-2xl bg-white px-8 pb-8 pt-[24px] text-left align-middle shadow-xl transition-all overflow-visible">
                  <div className="d-col">
                    <div className="flex justify-between items-center mb-[22px]">
                      <button
                        className="font-inter-regular text-[16px] text-hh-accent-dark"
                        onClick={() => {
                          setWarning(false);
                          setSelectedCustomDate(null);
                        }}
                      >
                        <FontAwesomeIcon
                          icon={faChevronLeft}
                          size="xs"
                          className="mr-2"
                          style={{
                            color: "#130f26",
                          }}
                        />
                        Back
                      </button>

                      <button
                        type="button"
                        onClick={() => setModal(false)}
                        className="btn-style"
                      >
                        <FontAwesomeIcon
                          style={{ width: 16, height: 26 }}
                          icon={faXmark}
                          size="2x"
                          color="#BBC0C5"
                        />
                      </button>
                    </div>

                    <h2 className="text-2xl font-inter-bold text-dark-gray">
                      Set Date for Subsidy Changes
                    </h2>

                    <span className="bg-light-gray d-col mt-[16px] py-[12px] rounded-lg">
                      {bulkUsers?.length > 1 && (
                        <small className="font-inter-regular text-dark-gray text-[16px] capitalize bg-light-gray px-[8px] mb-[8px] rounded-lg">
                          {bulkUsers?.length} Users
                        </small>
                      )}

                      <small className="font-inter-regular text-dark-gray text-[16px] capitalize bg-light-gray px-[8px] rounded-lg">
                        {subsidyType == "daily" ? (
                          <>
                            {[
                              { key: "monday", label: "Mon" },
                              { key: "tuesday", label: "Tue" },
                              { key: "wednesday", label: "Wed" },
                              { key: "thursday", label: "Thu" },
                              { key: "friday", label: "Fri" },
                              { key: "saturday", label: "Sat" },
                              { key: "sunday", label: "Sun" },
                            ]
                              .filter(
                                (day) => Number(dailySubsidy?.[day.key]) > 0
                              ) // 🟢 skip 0 / "0.00"
                              .map((day) => (
                                <span key={day.key}>
                                  {day.label}{" "}
                                  {priceFormatter(dailySubsidy[day.key])}{" "}
                                </span>
                              ))}
                          </>
                        ) : (
                          `Weekly ${priceFormatter(weeklySubsidy)}`
                        )}
                      </small>

                      <small className="font-inter-regular text-dark-gray text-[16px] bg-light-gray mt-[8px] px-[8px] rounded-lg">
                        These changes will apply to all future meals in the
                        series.
                      </small>
                    </span>

                    <strong className="text-dark-gray font-inter-semibold text-[16px] my-[16px]">
                      When should they take effect?
                    </strong>

                    <div className="d-col rounded-lg">
                      <div key="next" className="flex items-center">
                        <input
                          name="date-method"
                          type="radio"
                          value="next"
                          checked={changeEffect == "next"}
                          onChange={(e) => setChangeEffect(e.target.value)}
                          className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                        />
                        <label
                          htmlFor="photo"
                          className="ml-3 block text-sm font-inter-regular leading-6 text-gray-900"
                        >
                          Next available meal in series:{" "}
                          {nextAvailableMeal?.mealDate}
                        </label>
                      </div>

                      <div key="custom" className="flex items-center mt-[16px]">
                        <input
                          name="date-method"
                          type="radio"
                          value={"custom"}
                          checked={changeEffect == "custom"}
                          onChange={(e) => setChangeEffect(e.target.value)}
                          className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                        />
                        <label
                          htmlFor="signature"
                          className="ml-3 block text-sm font-inter-regular leading-6 text-gray-900"
                        >
                          Pick a custom date
                        </label>
                      </div>
                    </div>

                    {changeEffect == "custom" && (
                      <div
                        className="flex flex-row justify-center items-center mt-[16px]"
                        ref={calendarRef}
                      >
                        <Calendar
                          highlightToday={true}
                          className="shadow-lg"
                          isMulti={false}
                          onChange={handleDateChange}
                          disableYearPicker={true}
                          monthYearSeparator={" "}
                          mapDays={({ date }) => {
                            const allowedDates = validMeals?.map(
                              (meal) => meal?.meal_date
                            );
                            const formatted = dayjs(date.toDate()).format(
                              "YYYY-MM-DD"
                            );
                            const isEnabled = allowedDates.includes(formatted);

                            return {
                              disabled: !isEnabled,
                              style: isEnabled
                                ? { color: "#000" }
                                : {
                                    color: "#ccc",
                                    textDecoration: "",
                                    pointerEvents: "none",
                                  },
                            };
                          }}
                        />
                      </div>
                    )}

                    <small className="text-secondary text-[16px] font-inter-regular mt-[16px] mb-[32px]">
                      Subsidy changes can only be made for the next available
                      day where no restaurants have been assigned yet. If you
                      need to make this change sooner contact us for support.{" "}
                    </small>
                  </div>

                  <div className="flex justify-center items-center mt-5">
                    <button
                      className="btn-outline btn-style w-full"
                      onClick={() => setModal(false)}
                    >
                      Cancel
                    </button>

                    <button
                      className={classNames({
                        "btn-purple btn-style px-6 ml-3 w-full": true,
                        "btn-disabled":
                          changeEffect === "custom" && !selectedCustomDate,
                      })}
                      onClick={onSavePress}
                      disabled={changeEffect == "custom" && !selectedCustomDate}
                    >
                      Request changes
                    </button>
                  </div>
                </Dialog.Panel>
              ) : (
                <Dialog.Panel className="d-col justify-between rounded-xl md:w-6/12 transform overflow-hidden sm:rounded-2xl md:rounded-2xl bg-white px-8 pb-8 pt-[24px] text-left align-middle shadow-xl transition-all">
                  <div className="d-row justify-between items-center mb-[8px]">
                    <h2 className="text-[32px] text-dark-gray font-inter-bold">
                      {bulkUserUpdate
                        ? `Change ${
                            bulkUsers?.length > 1 ? "Users" : "User"
                          } Subsidy`
                        : "Update Meal Plan default Subsidy"}
                    </h2>

                    <button
                      onClick={() => setModal(false)}
                      className="text-dark-gray hover:text-yellow-600"
                      aria-label="Close"
                    >
                      <span className="text-xl">&times;</span>
                    </button>
                  </div>

                  {isPresent(mealPlan?.attributes?.days) &&
                  isPresent(mealPlan?.attributes?.dates_to_add) ? (
                    <span className="font-inter-regular text-[16px] d-col mb-[11px] text-secondary">
                      This is the default meal subsidy applied to all dates in
                      this meal plan, including both recurring days and any
                      additional dates you manually add. Newly added dates will
                      automatically use this default subsidy and won’t be
                      included in any existing custom subsidy settings. For
                      enabled dates, you can still set a custom per-meal subsidy
                      or a weekly subsidy if needed.
                    </span>
                  ) : isPresent(mealPlan?.attributes?.days) ? (
                    <span className="font-inter-regular text-[16px] d-col mb-[11px] text-secondary">
                      These changes will apply to all future meals in the series
                      and not for a single specific day.
                      <small className="font-inter-regular text-[16px] text-dark-gray mt-[16px]">
                        Choose frequency
                      </small>
                    </span>
                  ) : (
                    <span className="font-inter-regular text-[16px] d-col mb-[11px] text-secondary">
                      Setting a subsidy gives users this subsidy amount for each
                      meal. If you want to set a different subsidy for a
                      specific day, you can do so by managing that meal directly
                      on the date you want to update.
                    </span>
                  )}

                  {isPresent(mealPlan?.attributes?.dates_to_add) && (
                    <div className="d-col bg-light-gray w-full p-[12px] rounded-lg">
                      <div
                        className={classNames(
                          "focus:outline-none py-[9px] px-[19px] rounded-md",
                          standardSubsidyError
                            ? "bg-white border border-red-500"
                            : "bg-white"
                        )}
                      >
                        <small className="text-[16px] mr-1 font-inter-medium">
                          $
                        </small>

                        <input
                          className="text-[16px] font-inter-medium bg-transparent h-full focus:outline-none text-black"
                          type="text"
                          placeholder="0.00"
                          onFocus={() => {
                            if (
                              standardSubsidy == "0.0" ||
                              standardSubsidy == 0.0
                            ) {
                              setStandardSubsidy("");
                            }
                          }}
                          onChange={(e) => {
                            const value = e.target.value;
                            const regex = /^[0-9]*\.?[0-9]*$/;
                            if (regex.test(value)) {
                              setStandardSubsidy(value);
                              setStandardSubsidyError(false);
                            }
                          }}
                          value={standardSubsidy}
                        />
                      </div>
                    </div>
                  )}

                  {isPresent(mealPlan?.attributes?.days) &&
                    isPresent(mealPlan?.attributes?.dates_to_add) && (
                      <label className="relative inline-flex items-center cursor-pointer mt-3 gap-2.5">
                        <input
                          type="checkbox"
                          checked={enablePerMealWeeklySubsidy}
                          className="sr-only peer"
                          onChange={(e) =>
                            setEnabledPerMealWeekly(e.target.checked)
                          }
                        />

                        <div className=" w-9 h-5 p-0.5 bg-gray-200 rounded-full peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:start-[2px] after:bg-white after:w-4 after:h-4 after:shadow after:border after:rounded-full after:transition-all  peer-checked:bg-indigo-500"></div>

                        <span className="text-slate-700 text-sm font-inter-medium leading-none">
                          Enable Per Meal/Weekly Subsidy for Recurring Weekly
                          Meals
                        </span>
                      </label>
                    )}

                  {(enablePerMealWeeklySubsidy ||
                    (isPresent(mealPlan?.attributes?.days) &&
                      !isPresent(mealPlan?.attributes?.dates_to_add))) && (
                    <div className="my-3">
                      <div className="d-row mb-[11px]">
                        <button
                          className={classNames({
                            "d-row items-center rounded-xl text-catering hover:text-white font-inter-medium mr-[10px]": true,
                            "btn-disabled-mode": subsidyType !== "daily",
                            "btn-blue-accent text-white":
                              subsidyType == "daily",
                          })}
                          onClick={() => {
                            setSubsidyType("daily");

                            if (
                              mealPlan &&
                              mealPlan?.attributes?.subsidy_type == "weekly"
                            )
                              setSubsidyManually({
                                monday: "",
                                tuesday: "",
                                wednesday: "",
                                thursday: "",
                                friday: "",
                                saturday: "",
                                sunday: "",
                              });
                          }}
                        >
                          Per Meal
                        </button>

                        <button
                          className={classNames({
                            "d-row items-center rounded-xl text-catering hover:text-white font-inter-medium": true,
                            "btn-disabled-mode": subsidyType !== "weekly",
                            "btn-blue-accent text-white":
                              subsidyType == "weekly",
                          })}
                          onClick={() => {
                            if (
                              mealPlan &&
                              mealPlan?.attributes?.subsidy_type == "daily"
                            )
                              setWeeklySubsidy("0.00");

                            setSubsidyType("weekly");
                          }}
                        >
                          Weekly
                        </button>
                      </div>

                      {subsidyType === "daily" ? (
                        <div className="d-col bg-light-gray p-[12px] rounded-lg">
                          <div className="grid grid-cols-7 gap-4 py-[12px] px-[10px]">
                            {Object.keys(dailySubsidy)?.map((day) => {
                              const disabled = !isDayEnabled(day);

                              return (
                                <div key={day} className="text-center rounded">
                                  <span className="text-sm font-inter-medium text-gray-700 capitalize">
                                    {day?.slice(0, 3)}
                                  </span>
                                  <div
                                    className={classNames(
                                      "d-row px-[14px] py-[7px] mt-[7px] rounded-lg items-center",
                                      disabled
                                        ? "bg-[#E4E5EA]"
                                        : errors?.[day]
                                        ? "bg-white border border-red-500"
                                        : "bg-white"
                                    )}
                                  >
                                    {!disabled ? (
                                      <>
                                        <small className="text-[13px] font-inter-medium">
                                          $
                                        </small>
                                        <input
                                          disabled={false}
                                          className="w-full text-[13px] ml-1 font-inter-medium focus:outline-none h-[40px] text-black"
                                          type="number"
                                          placeholder="0.00"
                                          value={dailySubsidy[day]}
                                          onChange={(e) =>
                                            e.target.value > -1 &&
                                            onChangeDaily(e.target.value, day)
                                          }
                                          onKeyDown={preventMinus}
                                          onPaste={preventPasteNegative}
                                          onWheelCapture={(e) => {
                                            e.target.blur();
                                          }}
                                        />
                                      </>
                                    ) : (
                                      <div className="w-full h-[40px]"></div> // just reserve space for layout symmetry
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <div className="d-col bg-light-gray w-full p-[12px] rounded-lg">
                          <div
                            className={classNames(
                              "focus:outline-none py-[9px] px-[19px] rounded-md",
                              weeklyError
                                ? "bg-white border border-red-500"
                                : "bg-white"
                            )}
                          >
                            <small className="text-[16px] mr-1 font-inter-medium">
                              $
                            </small>

                            <input
                              className="text-[16px] font-inter-medium bg-transparent h-full focus:outline-none text-black"
                              type="text"
                              placeholder="0.00"
                              onFocus={() => {
                                if (
                                  weeklySubsidy == "0.0" ||
                                  weeklySubsidy == 0.0
                                ) {
                                  setWeeklySubsidy("");
                                }
                              }}
                              onChange={(e) => {
                                const value = e.target.value;
                                const regex = /^[0-9]*\.?[0-9]*$/;
                                if (regex.test(value)) {
                                  setWeeklySubsidy(value);
                                  setWeeklyError(false);
                                }
                              }}
                              value={weeklySubsidy}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex-col justify-start items-start gap-[7px] mt-[24px] flex">
                    <label className="relative inline-flex items-center cursor-pointer gap-2.5">
                      <input
                        disabled={defaultLocation?.disableCreditCards}
                        type="checkbox"
                        checked={
                          defaultLocation?.disableCreditCards
                            ? false
                            : overPayment
                        }
                        className="sr-only peer"
                        onChange={(e) => setEnableOverpayment(e.target.checked)}
                      />

                      <div className=" w-9 h-5 p-0.5 bg-gray-200 rounded-full peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:start-[2px] after:bg-white after:w-4 after:h-4 after:shadow after:border after:rounded-full after:transition-all  peer-checked:bg-indigo-500"></div>

                      <span className="text-slate-700 text-sm font-inter-medium leading-none">
                        Overpayment
                      </span>
                    </label>

                    <div className="justify-start items-start gap-1 mt-[14px] inline-flex">
                      <img
                        src={InfoButtonGray}
                        className="w-3 h-3"
                        alt="DangerIcon"
                      />

                      <div className="w-full text-slate-500 text-xs font-inter-normal leading-none">
                        Allow users to use their personal credit card when going
                        over set allowance to change your settings, please
                        contact your hungerhub account admin.
                      </div>
                    </div>
                  </div>

                  <div className="d-row w-full items-center mt-[24px] justify-center">
                    <button
                      className="btn-light w-full btn-style py-3 px-5"
                      onClick={() => setModal(false)}
                    >
                      Cancel
                    </button>

                    <button
                      className="btn-purple w-full btn-style py-3 px-6 ml-3"
                      onClick={onSavePress}
                    >
                      Continue
                    </button>
                  </div>
                </Dialog.Panel>
              )}
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default observer(ChangeSubsidyMealPlan);
